import { ApolloQueryResult, useQuery } from '@apollo/client';
import { capitalize } from 'lodash';
import React, { useCallback } from 'react';
import AddIcon from '@atlaskit/icon/glyph/add';
import { DynamicResultTable, OpenSidebarButton, isAccess, isResourceAccess, useFeatureToggle } from '@css-xp/base';
import {
  FetchShipToPartiesForTransactionAccountQuery,
  FetchShipToPartiesForTransactionAccountQueryVariables,
  Maybe,
  PaymentMethod,
} from '@css-xp/graphtypes';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_MULTIPLE_TAX_IDS } from '@atlassian/cssxp-utils-generic';
import { SIDEBAR } from '../../../transaction-account';
import { fetchShippingAddressesForTransactionAccount } from './queries/fetch-ship-to-parties-for-transaction-account';
import './sold-to-address-table.scss';

interface SoldToAddressTableProps {
  transactionAccountId?: string;
  billToAddress?: CommerceAddress | null;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  hidePricingEligibility?: boolean;
  shouldForceAddAddress?: boolean;
}

const { SHIP_TO_PARTY_WRITE } = Permissions;

export const emptyStateShippingDetailsProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-sold-to-address-state',
};

const getColumns = () => {
  const columns = [
    {
      content: 'Company name',
      width: 15,
    },
    {
      content: 'Address',
      width: 25,
    },
    {
      content: 'Pricing eligibility',
      width: 15,
    },
    {
      content: 'Tax exempt',
      width: 10,
    },
    {
      content: 'Actions',
      width: 25,
    },
    {
      content: '',
      width: 10,
    },
  ];

  return {
    cells: columns,
  };
};

const formatAddressLabel = (node: CommerceAddress) => {
  let formatAddressLabel1 = '';
  let formatAddressLabel2 = '';
  if (node?.addressLine1) {
    formatAddressLabel1 += node.addressLine1;
  }
  if (node?.addressLine2) {
    formatAddressLabel1 += `, ${node.addressLine2}`;
  }
  if (node?.city) {
    formatAddressLabel2 += `${node.city}`;
  }
  if (node?.postcode) {
    formatAddressLabel2 += `- ${node.postcode}`;
  }
  if (node?.state) {
    formatAddressLabel2 += `, ${node.state}`;
  }
  if (node?.country) {
    formatAddressLabel2 += `, ${node.country}`;
  }
  return (
    <div>
      {formatAddressLabel1}
      <br></br>
      {formatAddressLabel2}
    </div>
  );
};

const getTaxExemptStatus = (item: CommerceAddress, isAlternateTaxationEnabled: boolean) => {
  const taxExemptionStatus = item?.taxExempt ? 'Yes' : 'No';
  if (isAlternateTaxationEnabled && item.taxLabels && item.taxLabels.length > 0) {
    const taxIds = item?.taxIds ?? [];
    const taxIdLabels = item.taxLabels[0]?.taxIdLabels;
    const displayName = taxIdLabels?.filter((tax) => tax?.displayName);
    return displayName?.length
      ? displayName?.reduce((prevVal, tax) => {
          const taxId = taxIds.filter((taxId) => taxId?.label === tax?.type);
          const taxExemption = taxId.length > 0 ? taxId[0]?.taxExempt : Boolean(item?.taxExempt);
          const taxExemptionStatus = taxExemption ? 'Yes' : 'No';
          return `${prevVal} ${tax?.displayName} (${taxExemptionStatus}) `;
        }, '')
      : 'No';
  }
  return taxExemptionStatus;
};

const SoldToAddressTable = ({
  transactionAccountId,
  billToAddress,
  refetch,
  availablePaymentMethods,
  hidePricingEligibility = false,
  shouldForceAddAddress = false,
}: SoldToAddressTableProps) => {
  const [isEnabled] = useFeatureToggle();
  const isAlternateTaxationEnabled = isEnabled(ENABLE_MULTIPLE_TAX_IDS);

  const { data, loading } = useQuery<
    FetchShipToPartiesForTransactionAccountQuery,
    FetchShipToPartiesForTransactionAccountQueryVariables
  >(fetchShippingAddressesForTransactionAccount, {
    variables: {
      id: transactionAccountId ?? '',
    },
    skip: !transactionAccountId,
  });

  const totalCount = data?.transactionAccount?.availableShipToParties?.totalCount ?? 0;
  const shipToParties = data?.transactionAccount.availableShipToParties?.nodes;

  const renderActionsOnTop = useCallback(() => {
    return [
      () =>
        shouldForceAddAddress ||
        (isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(SHIP_TO_PARTY_WRITE)) ? (
          <div className="add-sold-to-address add">
            <div className="add-action">
              <OpenSidebarButton
                sidebar={SIDEBAR.EDIT_SHIP_TO_PARTY}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                payload={{ transactionAccountId, billToAddress, refetch, hidePricingEligibility }}
                appearance="link"
                iconBefore={<AddIcon label="Add icon" size="small" />}
                testId="add-sold-to-address"
                className="add-btn"
                label="Add a new sold-to address"
              >
                Add a new sold-to address
              </OpenSidebarButton>
            </div>
            <span className="add-subtext">Sold-to address is sometimes referred to as shipping address.</span>
          </div>
        ) : (
          <div />
        ),
    ];
  }, [transactionAccountId, billToAddress, refetch]);

  const getRows = () => {
    return (shipToParties ?? []).map(
      //@ts-ignore
      (item: CommerceAddress, index: number) => {
        return {
          key: `row-${index}-${item?.id}`,
          cells: [
            {
              key: 'name' + index,
              content: item?.name ?? 'Not available',
            },
            {
              key: 'address' + index,
              content: formatAddressLabel(item),
            },
            {
              key: 'eligibility' + index,
              content: item?.shipToPriceEligibility ? capitalize(item?.shipToPriceEligibility) : 'Commercial',
            },
            {
              key: 'tax' + index,
              content: getTaxExemptStatus(item, isAlternateTaxationEnabled),
            },
            {
              key: 'actions' + index,
              content: item?.hasLinkedSubscriptions && (
                <OpenSidebarButton
                  sidebar={SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS}
                  payload={{
                    shipToAddress: item,
                    transactionAccountId,
                    availablePaymentMethods: availablePaymentMethods,
                  }}
                  appearance="link"
                  sidebarOptions={{
                    sidebarWidth: '968px',
                    isOverlay: true,
                  }}
                  testId="view-linked-subscriptions-action"
                  spacing="compact"
                  className="view-subscriptions-link"
                  label="View linked subscriptions and invoices"
                >
                  View linked subscriptions and invoices
                </OpenSidebarButton>
              ),
            },
            {
              key: 'edit' + index,
              content:
                shouldForceAddAddress ||
                (isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(SHIP_TO_PARTY_WRITE)) ? (
                  <OpenSidebarButton
                    sidebar={SIDEBAR.EDIT_SHIP_TO_PARTY}
                    payload={{
                      shipToAddress: item,
                      transactionAccountId,
                      billToAddress,
                      pricingEligibility: item?.shipToPriceEligibility,
                      refetch,
                      hidePricingEligibility,
                    }}
                    appearance="primary"
                    sidebarOptions={{
                      sidebarWidth: '600px',
                      isOverlay: true,
                    }}
                    testId="edit-ship-to-party-action"
                    spacing="compact"
                    label="Edit"
                  >
                    Edit
                  </OpenSidebarButton>
                ) : (
                  <div />
                ),
            },
          ],
        };
      }
    );
  };

  return (
    <div className="sold-to-address-container">
      <DynamicResultTable
        testId="shipping-details"
        head={getColumns()}
        rows={getRows()}
        emptyStateProps={emptyStateShippingDetailsProp}
        isLoading={loading}
        totalCount={totalCount}
        isSearchEnabled={false}
        isPaginationEnabled={false}
        actions={renderActionsOnTop()}
        boxShadow={false}
        className="sold-to-address-table"
      />
    </div>
  );
};

export default SoldToAddressTable;
